import React from 'react';
import {
  Modal,
  Stack,
  Text,
  Button,
  Group,
  Alert,
  Box,
  Badge,
  Divider,
  Paper
} from '@mantine/core';
import {
  IconUser,
  IconAlertTriangle,
  IconCheck,
  IconX,
  IconCalendar,
  IconClock,
  IconMapPin,
  IconTrash
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';

// =============================================================================
// TYPES AND INTERFACES
// =============================================================================

interface Assignment {
  id: string;
  title: string;
  status: 'PENDING' | 'CONFIRMED' | 'CANCELLED' | 'COMPLETED';
  user?: {
    id: string;
    name: string;
    email: string;
  };
  users?: {
    id: string;
    name: string;
  };
  doctor?: {
    id: string;
    name: string;
    fullName: string;
  };
  sessionTime?: {
    id: string;
    name: string;
    startTime: string;
    endTime: string;
  };
  session_times?: {
    id: string;
    name: string;
    startTime: string;
    endTime: string;
  };
  roomSessionTime?: {
    id: string;
    name: string;
    label: string;
    startMinutes: string;
    endMinutes: string;
  };
  room: {
    id: string;
    number: string;
    name: string;
  };
  date: string;
  note?: string;
}

interface ConfirmUnassignModalProps {
  opened: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  assignment: Assignment | null;
  isBulkMode?: boolean;
  assignmentCount?: number;
  loading?: boolean;
}

/**
 * M2: Confirm Unassign Modal - Confirm removal of assignment(s)
 * 
 * Features:
 * - Clear confirmation with assignment details
 * - Support for single and bulk unassignment
 * - Warning about the action being irreversible
 * - Proper validation and error handling
 * - Accessibility support
 */
const ConfirmUnassignModal: React.FC<ConfirmUnassignModalProps> = ({
  opened,
  onClose,
  onConfirm,
  assignment,
  isBulkMode = false,
  assignmentCount = 0,
  loading = false
}) => {
  const [submitting, setSubmitting] = React.useState(false);

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await onConfirm();
      
      notifications.show({
        title: isBulkMode ? 'Assignments Removed' : 'Assignment Removed',
        message: isBulkMode 
          ? `Successfully removed ${assignmentCount} assignments`
          : 'Assignment has been removed',
        color: 'green',
        icon: <IconCheck size={16} />
      });
      
      onClose();
    } catch (error) {
      notifications.show({
        title: 'Removal Failed',
        message: error instanceof Error ? error.message : 'Failed to remove assignment(s)',
        color: 'red',
        icon: <IconX size={16} />
      });
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = !submitting && !loading;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="sm">
          <IconTrash size={20} />
          <Text fw={600}>
            {isBulkMode ? 'Remove Multiple Assignments' : 'Remove Assignment'}
          </Text>
        </Group>
      }
      size="md"
      centered
      closeOnClickOutside={false}
      closeOnEscape={!submitting}
      trapFocus
      returnFocus
    >
      <Stack gap="md">
        {/* Warning Alert */}
        <Alert
          icon={<IconAlertTriangle size={16} />}
          title="Confirm Removal"
          color="orange"
          variant="light"
        >
          <Text size="sm">
            {isBulkMode 
              ? `This will remove ${assignmentCount} assignments. This action cannot be undone.`
              : 'This will remove the assignment. This action cannot be undone.'
            }
          </Text>
        </Alert>

        {/* Assignment Details (Single Mode) */}
        {!isBulkMode && assignment && (
          <Box>
            <Text size="sm" fw={500} mb="xs">Assignment Details</Text>
            <Stack gap="xs">
              <Group gap="sm">
                <IconUser size={16} color="var(--mantine-color-blue-6)" />
                <Text size="sm">
                  <strong>Doctor:</strong> {assignment.user?.name || assignment.users?.name || assignment.doctor?.name || 'Unknown'}
                </Text>
                <Badge 
                  size="sm" 
                  variant="light" 
                  color={assignment.status === 'CONFIRMED' ? 'green' : 'orange'}
                >
                  {assignment.status}
                </Badge>
              </Group>
              
              <Group gap="sm">
                <IconMapPin size={16} color="var(--mantine-color-purple-6)" />
                <Text size="sm">
                  <strong>Room:</strong> {assignment.room.number} - {assignment.room.name}
                </Text>
              </Group>
              
              <Group gap="sm">
                <IconClock size={16} color="var(--mantine-color-green-6)" />
                <Text size="sm">
                  <strong>Session:</strong> {assignment.sessionTime?.name || assignment.session_times?.name || 'Unknown'} ({assignment.sessionTime?.startTime || assignment.session_times?.startTime || '00:00'} - {assignment.sessionTime?.endTime || assignment.session_times?.endTime || '00:00'})
                </Text>
              </Group>
              
              <Group gap="sm">
                <IconCalendar size={16} color="var(--mantine-color-cyan-6)" />
                <Text size="sm">
                  <strong>Date:</strong> {new Date(assignment.date).toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </Text>
              </Group>

              {assignment.note && (
                <Paper mt="xs" p="sm" bg="gray.0" radius="md" withBorder>
                  <Text size="sm" c="dimmed">
                    <strong>Note:</strong> {assignment.note}
                  </Text>
                </Paper>
              )}
            </Stack>
          </Box>
        )}

        {/* Bulk Mode Info */}
        {isBulkMode && (
          <Box>
            <Text size="sm" fw={500} mb="xs">Bulk Removal</Text>
            <Text size="sm" c="dimmed">
              You are about to remove <strong>{assignmentCount} assignments</strong> from the schedule.
              All selected assignments will be permanently removed.
            </Text>
          </Box>
        )}

        <Divider />

        {/* Loading State */}
        {loading && (
          <Box ta="center" py="md">
            <Text size="sm" c="dimmed">
              Preparing removal...
            </Text>
          </Box>
        )}

        {/* Action Buttons */}
        <Group justify="flex-end" gap="sm">
          <Button
            variant="light"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            color="red"
            onClick={handleConfirm}
            disabled={!canSubmit}
            loading={submitting}
            leftSection={<IconTrash size={16} />}
          >
            {submitting 
              ? (isBulkMode ? 'Removing...' : 'Removing...') 
              : (isBulkMode ? `Remove ${assignmentCount} Assignments` : 'Remove Assignment')
            }
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default ConfirmUnassignModal;
